/**
 * Panel — flat content card with a title bar. Hairline borders, no shadows,
 * no nested cards. Used to group content inside a SurfaceShell.
 *
 * Pass loading / error / empty for the standard async state pattern; the
 * panel renders a consistent SurfaceState in place of children. Otherwise
 * children render normally.
 */
import { ReactNode } from "react";
import { cn } from "@/lib/utils";
import {
  SurfaceState,
  chooseSurfaceState,
  type SurfaceStateVariant,
} from "./SurfaceState";

interface PanelProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;

  /** Async state. When any of these resolve to a SurfaceState variant,
   * children are replaced by the standard loading / error / empty view. */
  loading?: boolean;
  error?: unknown;
  isError?: boolean;
  empty?: boolean;
  emptyTitle?: string;
  emptyDescription?: ReactNode;
  errorTitle?: string;
  onRetry?: () => void;
  skeleton?: "list" | "table" | "card" | "none";
}

export function Panel({
  title,
  subtitle,
  actions,
  children,
  className,
  bodyClassName,
  loading,
  error,
  isError,
  empty,
  emptyTitle,
  emptyDescription,
  errorTitle,
  onRetry,
  skeleton = "list",
}: PanelProps) {
  const variant: SurfaceStateVariant | null = chooseSurfaceState(
    { isLoading: loading, isError: isError ?? Boolean(error), error },
    Boolean(empty),
  );

  return (
    <section
      className={cn(
        "rounded border border-border bg-card overflow-hidden",
        className,
      )}
    >
      {(title || actions) && (
        <header className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-border">
          <div className="min-w-0">
            {title && (
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground">
                {title}
              </div>
            )}
            {subtitle && (
              <div className="mt-0.5 text-[11px] text-muted-foreground">{subtitle}</div>
            )}
          </div>
          {actions && <div className="flex items-center gap-1.5">{actions}</div>}
        </header>
      )}
      <div className={cn("p-4", bodyClassName)}>
        {variant ? (
          <SurfaceState
            variant={variant}
            error={error}
            onRetry={onRetry}
            skeleton={skeleton}
            title={variant === "empty" ? emptyTitle : errorTitle}
            description={variant === "empty" ? emptyDescription : undefined}
          />
        ) : (
          children
        )}
      </div>
    </section>
  );
}
